/**
 * WHAT HAPPENS TO A CERTIFICATE IS A DECISION, NOT A SIDE EFFECT OF TAKING ACCESS AWAY.
 *
 * A certificate is a statement about what a person did, not about what they paid. Somebody who
 * finished the work and then had their money refunded still finished the work; somebody who was
 * refunded because they never got in never did. Those two cases want opposite answers, and no
 * default can be right for both — so there is no default here, and every certificate the server
 * offers has to be answered before the operation can be submitted.
 *
 * The server enforces the same thing, in three separate refusals this component exists to make
 * unreachable:
 *
 *   400 certificate_choice_required     an offered identifier has no choice
 *   409 certificate_choice_unexpected   a choice was sent for something not offered
 *   400 certificate_choice_invalid      a value that is not `revoke` or `retain`
 *
 * `certificateChoicesComplete` is exported so the dialogs gate their submit button on the same
 * predicate rather than each deciding what "answered" means.
 *
 * ---------------------------------------------------------------------------------
 * THIS FILE ALSO HOLDS TWO PIECES OF DIALOG PLUMBING (`useOperationId`,
 * `OpsFailureNotice`) THAT DO NOT BELONG TO CERTIFICATES.
 *
 * They live here because they are the two rules every payment dialog has to get identically
 * right — one idempotency key per dialog, and `already_done` is not a failure — and four copies
 * of either would drift in the direction that submits a second write. There is no shared
 * "ops dialog" module in this slice to put them in and `api/paymentOps.ts` is the wire seam,
 * not a component module. If one is added, move them; nothing else needs to change.
 */

import { useState } from 'react';
import { Badge, Banner, Button } from '../../shared/ui';
import { formatDate } from '../../shared/lib/catalogue';
import {
  explainOpsFailure,
  newOperationId,
  type WireCertificateOffer,
  type WireCertificateResult,
} from '../api/paymentOps';

export type CertificateChoice = 'revoke' | 'retain';

/** Keyed by `WireCertificateOffer.identifier`, which is what the server matches on. */
export type CertificateChoiceMap = Record<string, CertificateChoice>;

const isChoice = (value: unknown): value is CertificateChoice =>
  value === 'revoke' || value === 'retain';

/**
 * Has every offered certificate been answered, and nothing else?
 *
 * BOTH HALVES MATTER, because the server has a different refusal for each. A missing answer is
 * `certificate_choice_required`; an answer for something that is not on offer — a stale key left
 * behind when the screen reloaded and the offer list changed — is `certificate_choice_unexpected`,
 * and that one is easy to produce by accident and impossible to explain from the error alone.
 */
export function certificateChoicesComplete(
  offers: readonly WireCertificateOffer[],
  choices: Readonly<Record<string, string>>,
): boolean {
  const offered = new Set(offers.map((offer) => offer.identifier));
  for (const identifier of offered) {
    if (!isChoice(choices[identifier])) return false;
  }
  for (const key of Object.keys(choices)) {
    if (!offered.has(key)) return false;
  }
  return true;
}

/** Which offered identifiers are still unanswered — named, so the operator can go and find them. */
export function unansweredCertificates(
  offers: readonly WireCertificateOffer[],
  choices: Readonly<Record<string, string>>,
): string[] {
  return offers.filter((offer) => !isChoice(choices[offer.identifier])).map((o) => o.identifier);
}

/**
 * The four RESULT decisions, in operator English.
 *
 * `already_revoked` and `nothing_to_revoke` ARE SUCCESSES. They mean the state the operator asked
 * for is the state that exists — the first because somebody revoked it earlier, the second
 * because there was never a certificate to take. Rendering either as an error would send an
 * operator chasing a problem that does not exist.
 */
export const CERTIFICATE_RESULT_LABEL: Record<string, string> = {
  retained: 'Kept',
  revoked: 'Revoked',
  already_revoked: 'Already revoked before this — nothing to do',
  nothing_to_revoke: 'There was no certificate to revoke',
};

const CERTIFICATE_RESULT_TONE: Record<string, 'success' | 'warning' | 'info'> = {
  retained: 'success',
  revoked: 'warning',
  already_revoked: 'info',
  nothing_to_revoke: 'info',
};

/** Never `undefined`, and never an empty string: an unknown decision is shown as itself. */
export function certificateResultLabel(decision: string): string {
  return CERTIFICATE_RESULT_LABEL[decision] ?? (decision || 'The academy did not say');
}

const CHOICE_OPTIONS: readonly { value: CertificateChoice; label: string; blurb: string }[] = [
  {
    value: 'retain',
    label: 'Keep the certificate',
    blurb:
      'They finished the work and the certificate stays valid. Anyone checking its verification link still sees it.',
  },
  {
    value: 'revoke',
    label: 'Revoke the certificate',
    blurb:
      'The certificate is invalidated. Its verification link will show that it is no longer valid, and re-granting access later does not bring it back.',
  },
];

/** `issued_at` is nullable. A blank cell here would read as "never issued", which is a fact. */
function issuedLabel(issuedAt: string | null): string {
  if (issuedAt === null || issuedAt === '') return 'Not issued yet';
  return formatDate(issuedAt) || issuedAt;
}

export function CertificateChoices({
  offers,
  value,
  onChange,
  disabled,
}: {
  offers: readonly WireCertificateOffer[];
  value: Readonly<CertificateChoiceMap>;
  onChange: (next: CertificateChoiceMap) => void;
  disabled?: boolean;
}) {
  // Nothing offered, nothing to decide. Rendering a heading with an empty list under it would
  // suggest a certificate exists somewhere off screen.
  if (offers.length === 0) return null;

  const pending = unansweredCertificates(offers, value);

  return (
    <section className="ops-certificates">
      <h4>
        {offers.length === 1 ? 'This learner holds a certificate' : 'Certificates this covers'}{' '}
        <Badge tone="warning">{offers.length}</Badge>
      </h4>
      <p className="admin-muted">
        A certificate is a statement about what a person did, not about what they paid, so what
        happens to it is a decision and not a side effect of removing access. There is deliberately
        no default: choose for each one.
      </p>

      {offers.map((offer) => {
        const chosen = value[offer.identifier];
        return (
          <div className="ops-certificate" key={offer.identifier}>
            <p className="ops-certificate__head">
              <code>{offer.identifier}</code>{' '}
              <Badge tone="neutral">{offer.kind || 'certificate'}</Badge>{' '}
              {offer.already_revoked ? <Badge tone="info">Already revoked</Badge> : null}
            </p>
            <p className="admin-muted">
              {issuedLabel(offer.issued_at)}
              {offer.status ? ` · ${offer.status}` : ''}
            </p>
            {offer.already_revoked && (
              <p className="admin-muted">
                This one was already revoked before today, so it still needs an answer but neither
                answer changes it. Revoking it again comes back as “already revoked”, which is a
                success and not an error.
              </p>
            )}
            <div className="ops-certificate__choices" role="group" aria-label={offer.identifier}>
              {CHOICE_OPTIONS.map((option) => (
                <label className="ops-choice" key={option.value}>
                  <input
                    type="radio"
                    // Grouped per certificate, not per dialog: two certificates are two
                    // independent decisions and must not deselect each other.
                    name={`certificate-choice:${offer.identifier}`}
                    value={option.value}
                    // NOT `checked={chosen === option.value || !chosen}` and nothing like it.
                    // Unanswered has to look unanswered, or "no default" is only a comment.
                    checked={chosen === option.value}
                    disabled={disabled}
                    onChange={() => onChange({ ...value, [offer.identifier]: option.value })}
                  />
                  <span>
                    <strong>{option.label}</strong>
                    <br />
                    <small className="admin-muted">{option.blurb}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>
        );
      })}

      {pending.length > 0 && (
        <Banner tone="warning">
          {pending.length === 1
            ? 'One certificate still needs an answer before this can be submitted: '
            : `${pending.length} certificates still need an answer before this can be submitted: `}
          {pending.join(', ')}
        </Banner>
      )}
    </section>
  );
}

/**
 * What actually happened to each certificate, after the write.
 *
 * THREE STATES, NOT TWO. `undefined` means the academy's answer said nothing about certificates
 * and we do not know; `[]` means it said none were covered. Collapsing them would let a dialog
 * claim "no certificates were affected" on the strength of not having looked.
 */
export function CertificateResults({
  results,
}: {
  results: readonly WireCertificateResult[] | undefined;
}) {
  if (results === undefined) {
    return (
      <Banner tone="warning">
        This answer said nothing about certificates, so we cannot tell you what happened to any.
        The audit trail for this operation records what was decided.
      </Banner>
    );
  }
  if (results.length === 0) {
    return <p className="admin-muted">No certificate was covered by this.</p>;
  }
  return (
    <ul className="ops-results">
      {results.map((result) => (
        <li key={`${result.kind}:${result.identifier}`}>
          <code>{result.identifier}</code>{' '}
          <Badge tone={CERTIFICATE_RESULT_TONE[result.decision] ?? 'neutral'}>
            {certificateResultLabel(result.decision)}
          </Badge>
          {result.mechanism ? <> <small className="admin-muted">{result.mechanism}</small></> : null}
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------- shared dialog plumbing ---- */

/**
 * ONE OPERATION ID PER DIALOG, minted when it opens and reused on every retry.
 *
 * A fresh id on retry defeats both the database constraint and Stripe's derived idempotency key,
 * which is how one refund becomes two — `newOperationId`'s own note says so. So it is minted in
 * a `useState` initialiser, which runs once per mount, and never in a submit handler.
 *
 * `newOperationId` THROWS on a browser with no `crypto.randomUUID`, deliberately: it will not
 * hand out an id that might collide. Thrown from a render that would take the whole screen down,
 * so it is caught here and returned as a refusal the dialog can render — the operator is told
 * this browser cannot start the operation, which is true, instead of seeing a blank page.
 */
export function useOperationId(): { id: string; problem: null } | { id: null; problem: string } {
  const [state] = useState<{ id: string; problem: null } | { id: null; problem: string }>(() => {
    try {
      return { id: newOperationId(), problem: null };
    } catch (error) {
      return {
        id: null,
        problem:
          error instanceof Error
            ? error.message
            : 'This browser cannot generate the identifier a payment operation needs, so nothing can be started from it.',
      };
    }
  });
  return state;
}

/** `already_done` is the operation having happened. A dialog must not offer to repeat it. */
export function opsAlreadyHappened(error: unknown): boolean {
  return explainOpsFailure(error).outcome === 'already_done';
}

/**
 * The server's own sentence, and the right next action under it.
 *
 * `already_done` is NOT a failure — the operation was recorded, the screen is simply behind — so
 * it is `info` rather than `danger` and the action offered is Reload, never a retry. A retry
 * would be a second write attempt against a constraint that exists because somebody once made
 * two refunds out of one.
 *
 * `stale_order_version` gets the same Reload, for the mirror-image reason: the operator's screen
 * is describing an order that has since changed, and re-submitting the same stale `version` can
 * only fail again.
 */
export function OpsFailureNotice({
  error,
  onReload,
}: {
  error: unknown;
  onReload?: () => void;
}) {
  const failure = explainOpsFailure(error);
  const alreadyDone = failure.outcome === 'already_done';
  const needsReload = alreadyDone || failure.code === 'stale_order_version';

  return (
    <Banner tone={alreadyDone ? 'info' : 'danger'}>
      {/* The server's sentence verbatim. It carries the measured numbers and the real
          deadlines; a local paraphrase is how a screen ends up confidently wrong. */}
      <p>{failure.message}</p>
      {failure.nextStep !== null && <p>{failure.nextStep}</p>}
      {needsReload && onReload && (
        <p>
          <Button variant="secondary" onClick={onReload}>
            Reload
          </Button>
        </p>
      )}
    </Banner>
  );
}
